/**
 * Serviço dedicado para gerenciar conexões entre produtos
 * Segue princípios SOLID e TDA (Tell, Don't Ask)
 */

import { Produto, OrigemProduto } from "@PN/inclusao";
import { connectXmlWithPedido } from "./Connector";

export interface ProdutoPedido {
  codProduto: string;
  descricao: string;
  quantidade: number;
  valorTotal: number;
  unidadeMedida?: string;
  observacao?: string;
  codProdForn?: string;
  idUnico?: string;
}

export interface ConexaoStats {
  totalPedido: number;
  conectados: number;
  percentualPedido: number;
  conexoesPossiveis: Array<{
    pedido: ProdutoPedido;
    tabela: any;
    codProdForn: string;
  }>;
}

/**
 * Serviço para gerenciar conexões entre produtos do pedido e itens XML
 * Segue SRP: Responsabilidade única de calcular conexões
 * Segue DRY: Reutiliza lógica do Connector
 * Segue TDA: Usa comandos em vez de perguntas
 */
export class ConexaoService {
  /**
   * Calcula estatísticas de conexão usando o Connector unificado
   * Segue DRY: Reutiliza lógica do Connector em vez de duplicar
   */
  static calculateStats(
    produtos: ProdutoPedido[],
    aux: any,
    draft: any
  ): ConexaoStats {
    try {
      // Converte ProdutoPedido para Produto (formato do Connector)
      const produtosConvertidos = this.convertToProduto(produtos);

      // Filtra itens XML da tabela
      const xmlItems = this.extractXmlItems(aux.produtoTabela);

      // Usa o Connector para calcular conexões (DRY)
      const resultado = connectXmlWithPedido({
        xmlItems,
        pedidoItems: produtosConvertidos,
        tabelaAtual: aux.produtoTabela || [],
        tipoNota: draft.opcoes?.tipoNota,
        // Callbacks vazios pois só queremos o resultado
        onUpdateTabela: () => {},
        onUpdateProdutoProtheus: () => {},
        onClearProdutoProtheus: () => {},
      });

      // Converte resultado do Connector para formato esperado
      const conexoesPossiveis = this.convertConnectorResult(
        resultado.conexoesAutomaticas
      );

      const totalPedido = produtosConvertidos.length;
      const conectados = resultado.stats.conectados;
      const percentualPedido =
        totalPedido > 0 ? (conectados / totalPedido) * 100 : 0;

      return {
        totalPedido,
        conectados,
        percentualPedido,
        conexoesPossiveis,
      };
    } catch (error) {
      console.warn("Erro no cálculo de conexão:", error);
      return this.createEmptyStats();
    }
  }

  /**
   * Converte ProdutoPedido para Produto (formato do Connector)
   * Segue SRP: Responsabilidade única de conversão
   */
  private static convertToProduto(produtos: ProdutoPedido[]): Produto[] {
    return produtos.map((p) => ({
      codProduto: p.codProduto,
      descricao: p.descricao,
      quantidade: p.quantidade,
      valorTotal: p.valorTotal,
      unidadeMedida: p.unidadeMedida || "",
      codProdForn: p.codProdForn || "",
      idUnico: p.idUnico || "",
      origem: OrigemProduto.PEDIDO,
      // Campos obrigatórios do Produto
      pedidoCompra: "",
      itemPedido: "",
      origemProduto: "",
      ncmProduto: "",
      grupoTributario: "",
    }));
  }

  /**
   * Extrai itens XML da tabela
   * Segue SRP: Responsabilidade única de extração
   */
  private static extractXmlItems(produtoTabela: any[]): Produto[] {
    return (
      produtoTabela?.filter(
        (item: any) =>
          item.origem === OrigemProduto.XML || item.origem === "xml"
      ) || []
    );
  }

  /**
   * Converte resultado do Connector para formato esperado
   * Segue SRP: Responsabilidade única de conversão
   */
  private static convertConnectorResult(conexoesAutomaticas: Produto[]) {
    return conexoesAutomaticas.map((produto) => ({
      pedido: {
        codProduto: produto.codProduto || "",
        descricao: produto.descricao || "",
        quantidade: produto.quantidade,
        valorTotal: produto.valorTotal,
        unidadeMedida: produto.unidadeMedida || "",
        codProdForn: produto.codProdForn || "",
        idUnico: produto.idUnico || "",
      },
      tabela: produto, // O próprio produto já contém os dados da tabela
      codProdForn: produto.codProdForn || "",
    }));
  }

  /**
   * Cria estatísticas vazias
   * Segue SRP: Responsabilidade única de criação de dados padrão
   */
  private static createEmptyStats(): ConexaoStats {
    return {
      totalPedido: 0,
      conectados: 0,
      percentualPedido: 0,
      conexoesPossiveis: [],
    };
  }

  /**
   * Verifica se um produto tem conexão com XML
   * Segue TDA: Usa comandos em vez de perguntas
   */
  static isProductConnected(
    produto: ProdutoPedido,
    conexoesPossiveis: Array<{
      pedido: ProdutoPedido;
      tabela: any;
      codProdForn: string;
    }>
  ): boolean {
    const produtosConectadosSet = new Set(
      conexoesPossiveis.map((conexao) => conexao.pedido.codProduto)
    );
    return produtosConectadosSet.has(produto.codProduto);
  }

  /**
   * Filtra produtos sem conexão
   * Segue SRP: Responsabilidade única de filtragem
   */
  static filterUnconnectedProducts(
    produtos: ProdutoPedido[],
    conexoesPossiveis: Array<{
      pedido: ProdutoPedido;
      tabela: any;
      codProdForn: string;
    }>
  ): ProdutoPedido[] {
    const produtosComCodForn = produtos.filter((p) => p.codProdForn?.trim());
    const produtosConectadosSet = new Set(
      conexoesPossiveis.map((c) => c.pedido.codProduto)
    );

    return produtosComCodForn.filter(
      (p) => !produtosConectadosSet.has(p.codProduto)
    );
  }
}
